// theme/assets/cart-drawer.js — slice-7
// Web component for the cart drawer.
// DialogComponent is inlined here (no cross-file import).

class DialogOpenEvent extends Event {
  static eventName = 'dialog:open';
  constructor() { super(DialogOpenEvent.eventName, { bubbles: true }); }
}

class DialogCloseEvent extends Event {
  static eventName = 'dialog:close';
  constructor() { super(DialogCloseEvent.eventName, { bubbles: true }); }
}

class DialogComponent extends HTMLElement {
  get #dialogEl() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    this.#dialogEl?.addEventListener('close', this.#onNativeClose);
  }

  disconnectedCallback() {
    this.#dialogEl?.removeEventListener('close', this.#onNativeClose);
  }

  #onNativeClose = () => {
    this.dispatchEvent(new DialogCloseEvent());
  };

  showDialog() {
    const dialog = this.#dialogEl;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDialog() {
    const dialog = this.#dialogEl;
    if (!dialog || !dialog.open) return;
    dialog.close();
  }
}

const MOBILE_BREAKPOINT_PX = 640;
const isMobile = () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;

class CartDrawerComponent extends DialogComponent {
  #historyAbortController = null;

  get #body() {
    return this.querySelector('[data-cart-drawer-body]');
  }

  get #live() {
    return this.querySelector('[data-cart-live]');
  }

  get #dialog() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    // Close button
    this.querySelector('[data-cart-drawer-close]')?.addEventListener('click', () => this.closeDialog());

    // External cart events
    document.addEventListener('cart:add', this.#handleCartAdd);

    // Dialog lifecycle
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // Header trigger
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((btn) => {
      btn.addEventListener('click', () => this.showDialog());
    });

    // Load cart recommendations on connect + after every refresh
    this.#loadRecommendations();
    this.addEventListener('cart:refresh-done', this.#loadRecommendations);
    document.addEventListener('cart:change', this.#loadRecommendations);

    // ?cart-drawer-open=1 → open at load (for axe-core / dev preview)
    if (new URLSearchParams(location.search).get('cart-drawer-open') === '1') {
      this.showDialog();
    }

    // Clear stale history state at boot
    if (history.state?.cartDrawerOpen) history.replaceState(null, '');
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('cart:add', this.#handleCartAdd);
    this.#historyAbortController?.abort();
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) this.showDialog();
    this.refresh();
    this.#announceCount(event.detail?.item_count);
  };

  async refresh() {
    try {
      const res = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('section-render-' + res.status);
      const data = await res.json();

      if (data['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newBody = doc.querySelector('[data-cart-drawer-body]');
        const oldBody = this.#body;
        if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;
      }

      this.dispatchEvent(new CustomEvent('cart:refresh-done', {
        detail: { source: 'add-or-change' },
        bubbles: true,
      }));
    } catch (err) {
      console.warn('[cart-drawer] refresh failed; keeping optimistic state', err);
    }
  }

  #announceCount(itemCount) {
    if (itemCount == null) return;
    const live = this.#live;
    if (!live) return;
    const tmpl = (window.Theme?.translations?.cart_updated_announcement) ||
                 'Panier mis à jour. {{ count }} article(s) au total.';
    live.textContent = tmpl.replace('{{ count }}', String(itemCount));
  }

  #handleHistoryOpen = () => {
    if (!isMobile()) return;
    if (!history.state?.cartDrawerOpen) history.pushState({ cartDrawerOpen: true }, '');
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) history.back();
  };

  #handlePopState = async () => {
    if (this.#dialog?.open) await this.closeDialog();
  };

  #loadRecommendations = async () => {
    const slot = this.querySelector('[data-cart-recommendations]');
    if (!slot) return;
    const url = slot.dataset.recommendationsUrl;
    if (!url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      slot.innerHTML = html;
    } catch (err) {
      console.warn('[cart-drawer] recommendations load failed', err);
    }
  };
}

if (typeof customElements !== 'undefined' && !customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
